// import { InferenceSetup } from "@/inference/setup";
import { InferenceType } from "@/inference/types/inference";
import { SectionInferenceAttributes } from "@/inference/types/section";
import { EventPayloads } from "../types";
import { ScoringHandler } from "../scoring";

export type AgeGroups = "YOUNG" | "MIDDLE" | "OLD";

function getBehavioralInferenceFromLocalStorage() {
  const inference = sessionStorage.getItem("behavioralInference") || null;

  if (!inference) {
    return {
      age: { order: [], details: {} },
      accessTimeAge: { order: [], details: {} },
      deviceAge: { order: [], details: {} },
      scrollSpeedAge: { order: [], details: {} },
      viewTimeAge: { order: [], details: {} },
    };
  }

  try {
    return (
      JSON.parse(inference) || {
        age: { order: [], details: {} },
        accessTimeAge: { order: [], details: {} },
        deviceAge: { order: [], details: {} },
        scrollSpeedAge: { order: [], details: {} },
        viewTimeAge: { order: [], details: {} },
      }
    );
  } catch (e) {
    console.error("NOT IN JSON FORMAT");
    return {
      age: { order: [], details: {} },
      accessTimeAge: { order: [], details: {} },
      deviceAge: { order: [], details: {} },
      scrollSpeedAge: { order: [], details: {} },
      viewTimeAge: { order: [], details: {} },
    };
  }
}

function getInferenceFromLocalStorage(): InferenceType | null {
  const inference = sessionStorage.getItem("inference") || null;

  if (!inference) return null;

  try {
    return JSON.parse(inference) || null;
  } catch (e) {
    console.error("NOT IN JSON FORMAT");
    return null;
  }
}

export class ViewTimeInference {
  public static inference = getInferenceFromLocalStorage();
  static instance: ViewTimeInference | undefined;
  static observers: IntersectionObserver[] = [];

  static viewingCurrently: string[] = [];

  static categorySpecificScoreFlattened: Record<
    string,
    Record<string, number>
  > = {};
  static categorySpecificScore: Record<string, any> = {};

  static lastViewTime: Record<string, number> = {};

  static seenSection: Record<string, boolean> = {};
  static sectionScore: Record<string, number> = {};
  static totalViewTime: Record<string, number> = {};
  static flattenedScoreTable: Record<string, number> = {};

  static behavioralInference: {
    [key in
      | "age"
      | "viewTimeAge"
      | "deviceAge"
      | "accessTimeAge"
      | "scrollSpeedAge"]: {
      order: AgeGroups[];
      details: Record<AgeGroups, number>;
    };
  } = getBehavioralInferenceFromLocalStorage();

  static SCORE_MAPPING = {
    view_section: {
      ignoreBelow: 1,
      multiplier: 1,
    },
  };

  constructor() {}

  static getScore(
    sectionId: string,
    type: keyof EventPayloads,
    props: {
      sectionsInView: number;
      timeSpent: number;
    },
  ) {
    switch (type) {
      case "view_section":
        return ScoringHandler.Section.scoreFromView(sectionId, props);
      case "view_product":
        return ScoringHandler.Product.scoreFromView(sectionId, props);
      default:
        return 0;
    }
  }

  static getInstance() {
    if (!ViewTimeInference.instance) {
      ViewTimeInference.instance = new ViewTimeInference();
    }
    return ViewTimeInference.instance;
  }

  static getSectionInfo(sectionId: string): SectionInferenceAttributes {
    const obj = window.helium.sectionInfo;

    return obj[sectionId];
  }

  public static track(payload: EventPayloads[keyof EventPayloads]) {
    if (payload.event === "view_product" || payload.event === "view_section") {
      const sectionId =
        payload.event === "view_product"
          ? payload.product.handle
          : payload.section.id;

      if (!sectionId || !ViewTimeInference.lastViewTime[sectionId]) return;

      const currentTime = Date.now();
      const isRepeating =
        window.helium.logs &&
        window.helium.logs.at(-1)?.section?.id === sectionId;

      const hasSeenTooRecently =
        window.helium.logs &&
        window.helium.logs.some(
          (e) =>
            e?.section?.id === sectionId &&
            Math.abs(e?.time - currentTime) <= 100,
        );

      if (isRepeating || hasSeenTooRecently) return;

      ViewTimeInference.seenSection[sectionId] = true;

      ViewTimeInference.sectionScore[sectionId] =
        (ViewTimeInference.sectionScore[sectionId] ?? 0) +
        ViewTimeInference.getScore(sectionId, payload.event, {
          timeSpent: payload.view_time,
          sectionsInView: payload.sectionsInView || 1,
        });

      ViewTimeInference.totalViewTime[sectionId] =
        (ViewTimeInference.totalViewTime[sectionId] ?? 0) + payload.view_time;

      ViewTimeInference.viewingCurrently =
        ViewTimeInference.viewingCurrently.filter((e) => e !== sectionId);

      ViewTimeInference.lastViewTime[sectionId] = 0;

      window.helium.logs = [
        ...(window.helium.logs ?? []),
        {
          time: currentTime,
          section: {
            id: sectionId,
          },
          total_view_time: ViewTimeInference.totalViewTime[sectionId],
          view_time: payload.view_time,
          funnel_stage: sessionStorage.getItem("funnelStage") ?? "viewer",
        },
      ];

      return;
    }
  }

  public static getScoreTable(
    sectionInfo: SectionInferenceAttributes,
    score: number,
  ) {
    function flattenObjectWithScoreValues(obj: Record<string, any>) {
      if (Array.isArray(obj)) {
        return Object.fromEntries(obj.map((e) => [e, score]));
      }

      const flattened = {};

      for (const key in obj) {
        const value = obj[key];

        const regex = /\.$/;
        if (typeof value === "object") {
          const flattenedValue = flattenObjectWithScoreValues(value);

          for (const subKey in flattenedValue) {
            flattened[`${key}~${subKey}`] = flattenedValue[subKey];
          }
        } else {
          flattened[`${key}~${value}`.toLowerCase()] = score;
        }
      }

      return flattened;
    }

    const flatScoreTable = ViewTimeInference.flattenedScoreTable;

    const subcategory = (
      sectionInfo?.sectionInferenceAttributes?.subcategory || "unknown"
    ).toLowerCase();

    const categoryScore =
      ViewTimeInference.categorySpecificScoreFlattened[subcategory] || {};

    const sectionScore = flattenObjectWithScoreValues(sectionInfo);

    for (const flattenedKey in sectionScore) {
      flatScoreTable[flattenedKey] =
        (flatScoreTable[flattenedKey] ?? 0) + score;

      categoryScore[flattenedKey] = (categoryScore[flattenedKey] ?? 0) + score;
    }

    function unflatten(data: any) {
      const result = {};

      for (const i in data) {
        if (
          [
            "maintenance_instructions",
            "installation_instructions",
            "weight",
          ].some((e) => i.includes(e))
        ) {
          continue;
        }

        const keys = i.split("~").map((e) => e.trim());
        keys.reduce((acc, key, idx) => {
          try {
            return (
              acc[key] || (acc[key] = idx === keys.length - 1 ? data[i] : {})
            );
          } catch (e) {
            console.log(
              {
                acc,
                key,
                i,
                keys,
                arr: [
                  "maintenance_instructions",
                  "installation_instructions",
                  "weight",
                ],
              },
              "ERRORED IN VIEWTIME ",
            );
            return acc;
          }
        }, result);
      }

      return result;
    }

    ViewTimeInference.flattenedScoreTable = flatScoreTable;
    ViewTimeInference.categorySpecificScoreFlattened[subcategory] =
      categoryScore;

    const unflattenedSectionScore = unflatten(flatScoreTable) as InferenceType;
    ViewTimeInference.categorySpecificScore[subcategory] =
      unflatten(categoryScore)?.sectionInferenceAttributes;

    return {
      unflattenedSectionScore,
    };
  }
}
